import { ReactElement, useCallback, useEffect, useState } from 'react';
import { type TransProps, useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Trans } from '@/UI/Trans/Trans';
import { Link } from '@/UI/Link/Link';
import {
  AppDialog,
  AppDialogSection,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogContent,
  AppDialogActions,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { I18N_KEYS } from '@/language/keys';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { ZIndex } from '@/consts/z-indexes.consts';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { Flex, FlexCol } from '../styles';
import { TextStdRg } from '../AppTypography/AppTypography';
import { AppCheckbox } from '../AppCheckbox/AppCheckbox';
import type { ConfirmationDialogData } from '@/state/confirmation-dialog.state';
import type { AppDialogWidth } from '@/UI/AppDialog/AppDialog';

const LINK_PROPS = {
  target: '_blank',
  rel: 'noopener noreferrer',
  sx: {
    '&:hover': {
      textDecoration: 'none',
    },
  },
};

const AppConfirmationDialogMessage = ({
  message,
  messageData,
}: {
  message?: string;
  messageData?: {
    i18n?: TransProps<string, string | readonly string[], string | undefined, string>;
    data: Record<string, string | ReactElement | undefined>;
  };
}) => {
  if (messageData) {
    const components: Record<string, ReactElement> = {};
    for (const [key, value] of Object.entries(messageData.data || {})) {
      if (typeof value === 'string') {
        components[key] = <Link href={value} {...LINK_PROPS} />;
      } else if (value) {
        components[key] = value;
      }
    }

    return (
      <TextStdRg color={token.text} whiteSpace="pre-wrap">
        <Trans {...messageData.i18n} components={components} />
      </TextStdRg>
    );
  } else if (message) {
    return <TextStdRg color={token.text} text={message} whiteSpace="pre-wrap" />;
  }
};

interface AppConfirmationDialogStandaloneProps {
  dialogData: Omit<ConfirmationDialogData, 'onClose' | 'onConfirm'>;
  onConfirm: (checkboxChecked?: boolean) => void | Promise<void>;
  onClose: () => void;
}

/**
 * This is a standalone component. If you need to show it without using the global state for some reason, use this.
 */
export const AppConfirmationDialogStandalone = ({
  dialogData,
  onConfirm,
  onClose,
}: AppConfirmationDialogStandaloneProps) => {
  const {
    isOpen,
    title,
    message,
    messageData,
    confirmBtnText,
    cancelBtnText,
    destructive,
    disableBackdropClose,
    width,
    checkboxLabel,
    isCheckboxChecked: initialCheckboxChecked,
  } = dialogData;
  const resolvedWidth: AppDialogWidth = width ?? (checkboxLabel ? 'md' : 'sm');
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState('');
  const [checkboxChecked, setCheckboxChecked] = useState(initialCheckboxChecked ?? false);

  useEffect(() => {
    if (isOpen) {
      setCheckboxChecked(initialCheckboxChecked ?? false);
    }
  }, [isOpen, initialCheckboxChecked]);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    setErr('');
    try {
      await onConfirm(checkboxLabel ? checkboxChecked : undefined);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
      throw new Error(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm, checkboxLabel, checkboxChecked]);

  const handleClose = useCallback(
    (_event?: unknown, reason?: string) => {
      if (isLoading) return;
      if (disableBackdropClose && reason === 'backdropClick') return;
      onClose();
    },
    [disableBackdropClose, isLoading, onClose],
  );

  const actionButtons = (
    <>
      <ButtonContained size="small" mode="outlined" onClick={handleClose} disabled={isLoading}>
        {cancelBtnText || t(I18N_KEYS.GENERAL.CANCEL)}
      </ButtonContained>
      <ButtonContained
        size="small"
        mode={destructive ? 'filled-destructive' : undefined}
        onClick={() => void handleConfirm()}
        loading={isLoading}
        disabled={isLoading}
        data-testid="app-confirmation-dialog-confirm-btn"
      >
        {confirmBtnText || t(I18N_KEYS.GENERAL.CONFIRM)}
      </ButtonContained>
    </>
  );

  return (
    <AppDialog
      onClose={handleClose}
      open={isOpen}
      sx={{ zIndex: ZIndex.Overlays.CONFIRMATION_DIALOGS }}
      width={resolvedWidth}
      slotProps={{
        paper: {
          sx: {
            background: token.bg,
            px: 4,
            pt: 6,
            pb: 4,
            position: 'relative',
          },
        },
      }}
    >
      <AppDialogSection>
        <AppDialogHeader sx={{ p: 0, mb: 3 }}>
          <AppDialogTitle>{title}</AppDialogTitle>
          <AppDialogXBtn onClick={handleClose} sx={{ position: 'absolute', top: 12, right: 12 }} disabled={isLoading} />
        </AppDialogHeader>
        {/* Render the body only when there's something in it. Some confirmations are title-only */}
        {(message || messageData) && (
          <AppDialogContent sx={{ px: 0, pt: 0, pb: 4 }}>
            <FlexCol>
              <AppConfirmationDialogMessage message={message} messageData={messageData} />
            </FlexCol>
          </AppDialogContent>
        )}
        <AppDialogActions
          sx={{
            p: 0,
            justifyContent: 'flex-end',
            ...(checkboxLabel && { justifyContent: 'space-between', gap: 2 }),
          }}
        >
          {checkboxLabel && (
            <AppCheckbox
              checked={checkboxChecked}
              onChange={(_, checked) => setCheckboxChecked(checked)}
              label={checkboxLabel}
            />
          )}
          {checkboxLabel ? <Flex sx={{ gap: 1 }}>{actionButtons}</Flex> : actionButtons}
        </AppDialogActions>
        {err && <TextStdRg sx={{ mt: 1 }} text={err} whiteSpace="pre-wrap" color={token.textDanger} />}
      </AppDialogSection>
    </AppDialog>
  );
};

const AppConfirmationDialog = () => {
  const { confirmationDialogData, closeConfirmationDialog } = useConfirmationDialogStore();

  const handleConfirm = useCallback(
    async (checkboxChecked?: boolean) => {
      await confirmationDialogData.onConfirm(checkboxChecked);
      closeConfirmationDialog();
    },
    [closeConfirmationDialog, confirmationDialogData],
  );

  const handleClose = useCallback(() => {
    void confirmationDialogData.onClose?.();
    closeConfirmationDialog();
  }, [closeConfirmationDialog, confirmationDialogData]);

  if (!confirmationDialogData.isOpen) {
    return null;
  }

  return (
    <AppConfirmationDialogStandalone
      dialogData={confirmationDialogData}
      onConfirm={handleConfirm}
      onClose={handleClose}
    />
  );
};

export default AppConfirmationDialog;
